import { Injectable } from '@angular/core';
import { ExprForm } from '@mode-switch/express';
import { AllowedMarkChannels, EncField, Encoding, MarkType, ModifyField } from '@mode-vegas/vegas';
import { EncodingHelpers, FieldFormatHelpers, VegasFrame } from '@mode/charts/util-vegas';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledField } from '@mode/shared/util-switch';
import { UpdateFieldService } from '../encoding-field/update-field.service';
import * as MarksHelpers from '../marks-card/marks-helpers';

@Injectable({
  providedIn: 'root',
})
export class UpdateAllFieldService extends UpdateFieldService {
  updateFieldEncoding(
    view: FlamingoTypes.ModeView,
    encoding: Encoding,
    originalField: EncField,
    modifiedField: ModifyField
  ) {
    const fieldIds = this.getFieldIds(encoding, originalField);
    return EncodingHelpers.updateAllFieldEncoding(encoding, fieldIds, modifiedField);
  }

  clearFrame(encoding: Encoding, field: EncField) {
    return EncodingHelpers.clearFrames(encoding, this.getFieldIds(encoding, field));
  }

  selectFieldToFormat(field: EncField) {
    this.chartFacade.viewCalcFieldAllSetFieldToFormat(field);
  }

  makeFieldFormatsForTrend(trend: ExprForm | null, encoding: Encoding, field: EncField, view: FlamingoTypes.ModeView) {
    let fieldFormats: ChartTypes.FieldFormatMap | undefined = undefined;
    const fieldId = field.id;

    if (fieldId == null) {
      return;
    }
    let encResult = EncodingHelpers.loadEncoding(encoding);
    // Set the proper frame direction
    const fieldIds = this.getFieldIds(encoding, field);
    fieldIds.forEach((id) => {
      const encField = EncodingHelpers.getEncFieldById(encResult.encoding, id);
      if (encField) {
        const compiledField = CompiledField.fromEncField(view, encField);
        const frame = VegasFrame.makeDefaultExprFrame(encResult.encoding, compiledField);
        encResult = EncodingHelpers.setFrame(encResult.encoding, id, frame);
        if (trend === ExprForm.PctDifference || trend === ExprForm.PctOfTotal) {
          // Set the format to percent
          fieldFormats = { ...(fieldFormats ?? {}), ...FieldFormatHelpers.buildQuickCalcPercentFormat(encField) };
        }
      }
    });
    return { encResult, fieldFormats };
  }

  makeFieldFormatsForTableCalc(originalField: EncField, formula: CompiledField, encoding: Encoding) {
    // Set the format to percent if the formula was _changed_ to a Pct* formula
    let fieldFormats: ChartTypes.FieldFormatMap | undefined;
    const isPctFormula = formula.form === ExprForm.PctDifference || formula.form === ExprForm.PctOfTotal;
    if (isPctFormula) {
      const fieldIds = this.getFieldIds(encoding, originalField);
      fieldFormats = fieldIds
        .map((id) => EncodingHelpers.getEncFieldById(encoding, id))
        .reduce(
          (memo, encField) =>
            encField ? { ...memo, ...FieldFormatHelpers.buildQuickCalcPercentFormat(encField) } : memo,
          fieldFormats
        );
    }
    return fieldFormats;
  }

  getFieldIds(encoding: Encoding, field: EncField) {
    const summary = EncodingHelpers.getSummary(encoding);
    const channel = this.findChannel(encoding, field);
    return channel != null ? summary[MarksHelpers.markChannelIdsKey(channel)][field.id ?? ''] : [field.id ?? ''];
  }

  findChannel(encoding: Encoding, field: EncField) {
    const properties = encoding.marks.channel === '*' ? encoding.marks.properties : encoding.marks.series[0].properties;
    const markType = properties.type.value;
    const allowedChannels = AllowedMarkChannels[markType ?? MarkType.Bar];
    return allowedChannels.find((channel) => properties[channel].values.some((f) => f.id === field.id));
  }
}
